'use client';

import React, { FC, useState } from 'react';

import { AddButton } from '@/components/common/InventoryStatus';
import ProductValueProps from '@/components/common/ProductAddToCart/ProductValueProps';
import { buildPrimaryProductProperties } from '@/components/common/ProductAddToCart/utils';
import { TabNavigation } from '@/components/common/TabNavigation';

import { ERROR_KINDS, ITEM_ORIGINS } from '@/core/constants';
import { TypeProduct, TypeShipmentList } from '@/core/generated-types';

import { API_URLS } from '@/lib/api/config';
import { TypeSubscriptionPlanInstallmentsResponse } from '@/lib/api/subscriptionManager/types';

import {
  getProductInventoryStatus,
  ShopifyProduct,
  ShopifyProductType,
} from '@/lib/shopify';

import { usePathname } from '@/navigation';

import {
  PAGE_LOCATIONS,
  PRODUCT_ADDED_COMPONENT_TYPES,
} from '@/utils/rudderstack';
import { getDefaultVariant } from '@/utils/shopify';
import { handleCartFormSubmit } from '@/utils/shopify/handleCartFormSubmit';
import { Translations } from '@/utils/translations/generateTranslationObject';

import { EnhancedTypeTabContent } from '../TabNavigation/TabNavigation';

import ProductShipmentList from './ProductShipmentList';

import { usePlayGeniusCheckout } from '@/hooks/usePlayGeniusCheckout/usePlayGeniusCheckout';
import { PlayGeniusCheckoutSource } from '@/hooks/usePlayGeniusCheckout/usePlayGeniusCheckout';
import { useErrorDirectives } from '@/context/ErrorDirectiveContext';
import { findApplicableErrorDetails } from '@/components/common/Errors/utils';
import { Errors } from '@/components';
import { TariffPDPMessage } from '../Tariff';
import { TypeTariffSettings } from '../types';
import AddToCartErrorMessage from '../AddToCartErrorMessage/AddToCartErrorMessage';

export interface ProductOptionsAddToCartTabsProps {
  originalComponent: React.JSX.Element;
  alternativeProduct: TypeProduct;
  shopifyAlternativeProduct: ShopifyProduct;
  t: Translations;
  shipmentList?: TypeShipmentList;
  subscriptionPlanInstallments?: TypeSubscriptionPlanInstallmentsResponse;
  tariffSettings?: TypeTariffSettings;
}

enum TabIDs {
  BuyNow = 'buy-now',
  Subscribe = 'subscribe',
}

const ProductOptionsAddToCartTabs: FC<ProductOptionsAddToCartTabsProps> = ({
  originalComponent,
  alternativeProduct,
  shopifyAlternativeProduct,
  t,
  shipmentList,
  subscriptionPlanInstallments,
  tariffSettings,
}) => {
  const [isCheckoutProcessing, setIsCheckoutProcessing] =
    useState<boolean>(false);
  const [addToCartError, setAddToCartError] = useState<string | null>(null);

  const pathname = usePathname();

  const variant = getDefaultVariant(
    alternativeProduct.fields.title,
    shopifyAlternativeProduct.variants,
  );

  const inventoryStatus = getProductInventoryStatus(
    shopifyAlternativeProduct,
    variant,
  );

  const { continueToPlayGeniusCheckout, errorOccurred } = usePlayGeniusCheckout(
    PlayGeniusCheckoutSource.ProductPage,
  );

  const { errorDirectives } = useErrorDirectives();

  const {
    errors: checkoutRequestFailureErrors,
    heading: checkoutRequestFailureHeading,
  } = findApplicableErrorDetails(
    errorDirectives,
    ERROR_KINDS.FLOW_BLOCKING_API_FAILURE,
  );

  const { errors: coreDataFailureErrors, heading: coreDataFailureHeading } =
    findApplicableErrorDetails(
      errorDirectives,
      ERROR_KINDS.CORE_DATA_DEPENDENCY_FAILURE,
    );

  const defaultQuantity = 1;

  const shouldRedirectToPlaygeniusCheckout =
    shopifyAlternativeProduct?.productType ===
    ShopifyProductType.EARLY_READER_CLUB;

  function onSubmitSuccess() {
    if (shouldRedirectToPlaygeniusCheckout) {
      continueToPlayGeniusCheckout()
        .catch(console.error)
        .finally(() => setIsCheckoutProcessing(false));
    }
  }

  function handleSubscribeFormSubmit(e: React.FormEvent<HTMLFormElement>) {
    setAddToCartError(null);
    handleCartFormSubmit({
      e,
      shopifyProduct: shopifyAlternativeProduct,
      shopifyVariant: variant,
      productFields: alternativeProduct.fields,
      url: pathname,
      defaultQuantity,
      buttonLevel: PRODUCT_ADDED_COMPONENT_TYPES.PRIMARY_CTA,
      noRedirect: shouldRedirectToPlaygeniusCheckout,
      onSubmitSuccess,
      onSubmitFailure: () => {
        e.preventDefault();
        setAddToCartError(
          "This item wasn't added to your cart. Please try again.",
        );
      },
      onStartFormSubmission: () => {
        shouldRedirectToPlaygeniusCheckout && setIsCheckoutProcessing(true);
      },
    }).catch(console.error);
  }

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    handleSubscribeFormSubmit(e);
  }

  const customFirstTabContent = (
    <div
      data-testid="buy-now-form-tab"
      className="lg:mt-6 mt-3"
      key="buy-now-form-tab"
    >
      {originalComponent}
    </div>
  );

  const customSecondTabContent = (
    <div
      className="w-full lg:mt-6 mt-3"
      data-testid="subscribe-form-tab"
      key="subscribe-form-tab"
    >
      <div className="grid gap-y-3">
        <div className="px-[21px] lg:px-7 py-4 lg:py-7 grid gap-y-6 border border-grey-midtone rounded text-charcoal-v2">
          {shipmentList && (
            <ProductShipmentList
              shipmentList={shipmentList}
              priceLabel={t.tab_2_price_label}
              itemLabel={t.tab_2_list_item_label}
            />
          )}
        </div>

        <form
          id="add-to-cart-form"
          data-testid="subscribe-now-form"
          action={`${API_URLS.CART_SERVICE}/add`}
          method="post"
          onSubmit={onSubmit}
        >
          <input
            type="hidden"
            name="lines"
            value={JSON.stringify([
              {
                merchandiseId: variant.id,
                quantity: defaultQuantity,
                attributes: buildPrimaryProductProperties(
                  shopifyAlternativeProduct,
                  ITEM_ORIGINS.PRODUCT_PAGE,
                  subscriptionPlanInstallments?.data?.starting_installments,
                  alternativeProduct.fields.subscriptionPlanAnchor,
                ),
              },
            ])}
          />

          <AddButton
            inventoryStatus={inventoryStatus}
            product={alternativeProduct}
            isLoading={isCheckoutProcessing}
            isDisabledFromCaller={!subscriptionPlanInstallments?.ok}
          />

          <TariffPDPMessage
            product={alternativeProduct}
            tariffSettings={tariffSettings}
          />

          {!subscriptionPlanInstallments?.ok && (
            <Errors
              heading={coreDataFailureHeading}
              errors={coreDataFailureErrors}
              classes="mt-4 md:w-full"
            />
          )}

          {errorOccurred && (
            <Errors
              heading={checkoutRequestFailureHeading}
              errors={checkoutRequestFailureErrors}
              classes="mt-4 md:w-full"
            />
          )}

          {addToCartError && <AddToCartErrorMessage error={addToCartError} />}
        </form>
      </div>

      <ProductValueProps
        productValueProps={alternativeProduct.fields.productValueProps}
      />
    </div>
  );

  const tabs: EnhancedTypeTabContent[] = [
    {
      fields: {
        id: TabIDs.BuyNow,
        label: t.firstTabLabel,
        subLabel: t.firstTabSubLabel,
        textAlign: 'center',
        components: [],
        customComponent: [customFirstTabContent],
      },
    } as unknown as EnhancedTypeTabContent,
    {
      fields: {
        id: TabIDs.Subscribe,
        label: t.secondTabLabel,
        subLabel: t.secondTabSubLabel,
        textAlign: 'center',
        components: [],
        customComponent: [customSecondTabContent],
      },
    } as unknown as EnhancedTypeTabContent,
  ];

  return (
    <TabNavigation
      tabs={tabs}
      tabType="pill"
      eventPageLocation={PAGE_LOCATIONS.PURCHASE_OPTIONS}
    />
  );
};

export default ProductOptionsAddToCartTabs;
